/**
 * Hardware + shipping catalog resolution. Ported from quote-gen `catalog.py`.
 *
 * The governing rule: matching is EXACT (identifier, or an explicitly curated synonym)
 * and never fuzzy. Token-overlap matching was deliberately removed upstream because
 * "pro" silently mis-priced as "Pro Plus" — a wrong price on a customer-facing quote is
 * far worse than no price. A line that doesn't resolve is returned unmatched so the
 * caller can flag it; it is never approximated.
 */
import { toCents } from './cents';
import type {
  CatalogProduct,
  CatalogShipping,
  Currency,
  ShippingRegion,
  ShippingRateNode,
} from './types';

/** Normalize to lowercase words: "OptiKiosk 10\"" → "optikiosk 10". */
function norm(s: string | undefined | null): string {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Alphanumeric-only, no spaces — so "Pro Max Player" collapses to the same key as the
 * alias "promax-player". Keeps "Pro Max" from being matched against a "Pro ..." product.
 */
function collapse(s: string | undefined | null): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * How reps and customers actually name a product → its catalog alias. Resolved here so
 * the quote always shows the real catalog product name, never the operator's shorthand.
 *
 * Kept verbatim in sync with quote-gen `catalog.PRODUCT_SYNONYMS`. Note OptiStick IS the
 * OptiSigns Android Stick Player — the same first-party HDMI Android player.
 */
export const PRODUCT_SYNONYMS: Record<string, string[]> = {
  'android-stick': [
    'optistick',
    'opti stick',
    'optisigns optistick',
    'optisigns android stick',
    'optisigns android stick player',
    'optisigns android player',
    'android stick',
    'android stick player',
    'android player',
    'android tv stick',
    'hdmi stick',
  ],
  'promax-player': [
    'promax',
    'pro max',
    'pro max player',
    'promax player',
    'optisigns promax',
    'promax signage player',
    '8k player',
  ],
  'pro-player': [
    'pro player',
    'pro signage player',
    'optisigns pro player',
    'pro signage',
    '4k player',
  ],
  'optikiosk-10': ['optikiosk 10', 'kiosk 10', 'optisigns kiosk 10'],
  'optikiosk-32': ['optikiosk 32', 'kiosk 32', 'optisigns kiosk 32'],
  'optikiosk-50': ['optikiosk 50', 'kiosk 50', 'optisigns kiosk 50'],
};

const SYNONYM_TO_ALIAS: Record<string, string> = Object.entries(PRODUCT_SYNONYMS).reduce(
  (acc, [alias, terms]) => {
    for (const term of terms) acc[collapse(term)] = alias;
    return acc;
  },
  {} as Record<string, string>,
);

export interface MatchResult<T> {
  match: T | null;
  reason: string;
}

/**
 * The identity fields matching needs. Products carry all of them; shipping docs carry
 * only `_id` + `name` — which is why this is a structural minimum rather than
 * `CatalogProduct`. The Python original gets this for free from duck-typed dicts.
 */
export interface MatchableDoc {
  _id: string;
  name?: string;
  alias?: string;
  variantId?: string;
}

/** Exact match on `_id` / `variantId` / `alias` / `name` (raw or collapsed), then synonyms. */
function identMatch<T extends MatchableDoc>(ident: string, products: T[]): T | null {
  const ic = collapse(ident);
  for (const p of products) {
    if (ident === p._id || (p.variantId && ident === p.variantId)) return p;
    if (ic && (ic === collapse(p.alias) || ic === collapse(p._id) || ic === collapse(p.name))) {
      return p;
    }
  }
  const syn = SYNONYM_TO_ALIAS[ic];
  if (syn) {
    for (const p of products) {
      if (collapse(p.alias) === collapse(syn)) return p;
    }
  }
  return null;
}

/**
 * Resolve a line to exactly ONE catalog product. Tries the explicit identifier first,
 * then the free-text label — both through exact + synonym resolution only.
 *
 * An ambiguous free-text match (more than one product with the same normalized name)
 * counts as NO match: picking one arbitrarily is the silent-mismatch failure this
 * function exists to prevent.
 */
export function matchProduct<T extends MatchableDoc>(
  query: string | undefined,
  sku: string | undefined,
  products: T[],
): MatchResult<T> {
  for (const ident of [sku, query]) {
    if (!ident) continue;
    const p = identMatch(ident, products);
    if (p) return { match: p, reason: ident === sku ? 'sku' : 'resolved' };
  }

  const q = norm(query);
  if (q) {
    const exact = products.filter((p) => q === norm(p.name) || q === norm(p.alias));
    if (exact.length === 1) return { match: exact[0], reason: 'exact-name' };

    const qc = collapse(query);
    const coll = products.filter(
      (p) => qc && (qc === collapse(p.name) || qc === collapse(p.alias)),
    );
    if (coll.length === 1) return { match: coll[0], reason: 'collapsed-exact' };
  }

  const ident = sku || query;
  return { match: null, reason: ident ? `'${ident}' not in catalog` : 'nothing selected' };
}

/**
 * Unit price in cents for a currency. `prices[currency]` wins; otherwise the legacy
 * scalar `price`, flagged as a fallback so the caller can warn (it is USD, so using it
 * for a EUR quote is a real, visible problem — not something to swallow).
 */
export function resolveProductPrice(
  doc: CatalogProduct,
  currency: string,
): { cents: number | null; fallback: boolean } {
  const cur = (currency || 'usd').toLowerCase();
  const prices = doc.prices || {};
  const direct = prices[cur];
  if (direct !== undefined && direct !== null) return { cents: toCents(direct), fallback: false };
  if (doc.price !== undefined && doc.price !== null) {
    return { cents: toCents(doc.price), fallback: true };
  }
  return { cents: null, fallback: true };
}

/**
 * Discriminated on `error` so "resolved without an error" MEANS both rates are numbers.
 * Previously every caller had to null-check the rates even after checking `error`, and
 * removing one of those checks silently un-narrowed the type — the compiler now enforces
 * the invariant instead of relying on the defensive branch being remembered.
 */
export type ResolvedShipping =
  | { ok: false; error: string; firstCents: null; addlCents: null; fallback: false }
  | { ok: true; firstCents: number; addlCents: number; fallback: boolean };

/**
 * Shipping is region-tiered, NOT a flat currency map:
 *   `prices[region] = { price, priceFirstDevice, additionalPrice }`, each a currency map.
 *
 * Line total convention: `first + addl × (qty − 1)`. Where there is no first-device rate
 * (US Standard, for instance), `additionalPrice`/`price` IS the flat per-unit rate and
 * `first` collapses onto it — which is why 7 devices at $7.90 comes to $55.30, not a
 * first-device premium plus six.
 */
export function resolveShipping(
  doc: CatalogShipping,
  region: ShippingRegion,
  currency: string,
): ResolvedShipping {
  const reg = (region || 'internal').toLowerCase();
  // Nullable: live data stores `international: null` on Express to mean "not offered".
  const node: ShippingRateNode | null | undefined = (doc.prices || {})[reg];
  if (!node) {
    return {
      ok: false,
      firstCents: null,
      addlCents: null,
      fallback: false,
      // Express-international genuinely isn't offered — this is the path that catches it.
      error: `no '${reg}' shipping rate for '${doc.name ?? doc._id}'`,
    };
  }

  const cur = (currency || 'usd').toLowerCase();
  const pick = (
    m?: Record<string, number | null> | null,
  ): { cents: number | null; fb: boolean } => {
    if (!m || typeof m !== 'object') return { cents: null, fb: false };
    const direct = m[cur];
    if (direct !== undefined && direct !== null) return { cents: toCents(direct), fb: false };
    if (m.usd !== undefined && m.usd !== null) return { cents: toCents(m.usd), fb: true };
    return { cents: null, fb: false };
  };

  const per = pick(node.price);
  const addl = pick(node.additionalPrice);
  const first = pick(node.priceFirstDevice);

  const addlCents = addl.cents ?? per.cents;
  const firstCents = first.cents ?? addlCents;

  // A region key can EXIST while carrying no usable rate — Express international is
  // literally `{}` in deviceShippings, because it isn't offered. Reporting that as an
  // error here (rather than returning nulls) is what makes it explain itself: both
  // callers already surface `error`, whereas nulls previously produced a line that was
  // unpriced with no stated reason, leaving the operator to guess.
  if (addlCents === null || firstCents === null) {
    return {
      ok: false,
      firstCents: null,
      addlCents: null,
      fallback: false,
      error: `'${doc.name ?? doc._id}' has no ${reg} rate priced in '${cur}' or usd — it may not be offered for that region`,
    };
  }

  return {
    ok: true,
    firstCents,
    addlCents,
    fallback: per.fb || addl.fb || first.fb,
  };
}

/**
 * The default shipping service for an auto-added line, matched on the name keyword (the
 * two catalog options are "Standard Shipping & Handling" / "Express Shipping & Handling").
 * Falls back to the first active option so a renamed catalog degrades to a priced line
 * rather than no line.
 */
export function defaultShipping(
  shippings: CatalogShipping[],
  prefer: 'standard' | 'express' = 'standard',
): CatalogShipping | null {
  const key = prefer.toLowerCase();
  for (const s of shippings) {
    if ((s.name || '').toLowerCase().includes(key)) return s;
  }
  return shippings[0] ?? null;
}

/**
 * Ship-to region from the customer's country.
 *
 * An UNKNOWN or blank country resolves to `internal` (US), NOT international. Assuming
 * international silently overcharged US customers whose country wasn't recorded, and
 * domestic is both the cheaper and the likelier default.
 */
export function inferRegion(country: string | undefined): ShippingRegion {
  const US = new Set(['us', 'usa', 'unitedstates', 'unitedstatesofamerica', 'america']);
  const c = (country || '').trim().toLowerCase().replace(/\./g, '').replace(/\s/g, '');
  return c && !US.has(c) ? 'international' : 'internal';
}

export const CURRENCY_SET: ReadonlySet<string> = new Set<string>([
  'usd',
  'eur',
  'gbp',
  'cad',
  'aud',
] satisfies Currency[]);
